import { useState, useEffect } from "react";
import {
  ChevronRight,
  Map,
  Heart,
  Coffee,
  ShoppingBag,
  Leaf,
  Award,
  Wind,
} from "lucide-react";
import HomeHeader2 from "@/components/homePage/HomeHeader2";
import carte from "../Images/carte2.jpg";
import culture from "../Images/culture.png";
import canonique_epice from "../Images/canonique-epice.jpg";
import criquettes from "../Images/criquettes.jpg";
import artisanes from "../Images/artisanes.jpeg";
import panorama from "../Images/panorama.jpg";
import RichessesNigerSection from "./RichessesNigerSection";

import un from "../../src/image/1.jpg";
import deux from "../../src/image/2.jpg";
import trois from "../../src/image/3.jpg";

// Couleurs du drapeau du Niger (reprises de votre code existant)
const COLORS = {
  orange: "#E87E04",
  white: "#FFFFFF",
  green: "#008751",
  darkGreen: "#006B3F",
  lightOrange: "#FFE0B2",
  lightGreen: "#CCFFCC",
  gray: "#718096",
  lightGray: "#E2E8F0",
  paleGreen: "#E6FFEA",
};

// Images pour les différentes sections (à remplacer par vos propres images)
const IMAGES = {
  hero: panorama, // Image panoramique du Niger
  map: carte, // Carte du Niger
  culture: culture, // Image culturelle
  spices: canonique_epice, // Image d'épices/Yaji
  crickets: criquettes, // Image de Criquets
  artisans: artisanes, // Image d'artisans
};

const ii = [
  {
    src: un,
    alt: "Danse traditionnelle nigérienne",
    title: "Danse traditionnelle",
  },
  {
    src: deux,
    alt: "Célébrations de mariage au Niger",
    title: "Célébrations de mariage",
  },
  {
    src: trois,
    alt: "Tenue traditionnelle Haoussa",
    title: "Tenue traditionnelle",
  },
];

export default function AboutNigerPage() {
  const [activeTab, setActiveTab] = useState("culture");
  const [animatedCount, setAnimatedCount] = useState({
    population: 0,
    area: 0,
    yearFounded: 0,
    ethnicGroups: 0,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Animation des chiffres pour la section statistiques
  useEffect(() => {
    const final = {
      population: 25,
      area: 1267000,
      yearFounded: 1960,
      ethnicGroups: 8,
    };

    const duration = 2000; // 2 secondes pour l'animation
    const steps = 50; // nombre d'étapes
    const interval = duration / steps;

    let step = 0;

    const timer = setInterval(() => {
      step++;
      const progress = step / steps;

      setAnimatedCount({
        population: Math.floor(final.population * progress),
        area: Math.floor(final.area * progress),
        yearFounded: Math.floor(1960 + (final.yearFounded - 1960) * progress),
        ethnicGroups: Math.floor(final.ethnicGroups * progress),
      });

      if (step >= steps) {
        clearInterval(timer);
        setAnimatedCount(final);
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  // Contenu des onglets
  const tabContent = {
    culture: {
      title: "Culture & Traditions",
      content: (
        <div className="space-y-4">
          <p>
            Le Niger possède un riche patrimoine culturel issu de ses
            différentes communautés ethniques. La culture nigérienne est
            fortement influencée par ses groupes ethniques principaux : les
            Haoussa, les Zarma-Songhaï, les Touareg, les Peuls, les Kanouri, les
            Toubou, les Gourmantché et les Arabes.
          </p>
          <p>
            La musique traditionnelle occupe une place importante dans la vie
            quotidienne des Nigériens, avec des instruments comme le tambour
            d'aisselle (kalangou), la flûte peule, et le luth à trois cordes
            (molo). Les danses traditionnelles accompagnent les cérémonies et
            les festivités, chaque ethnie ayant ses propres styles.
          </p>
          <p>
            L'artisanat nigérien est reconnu pour sa qualité et sa diversité :
            maroquinerie touareg, poterie, vannerie, bijoux en argent, et
            tissages aux motifs symboliques qui racontent des histoires ou
            représentent des aspects de la vie quotidienne.
          </p>
          <div className="hidden md:grid md:grid-cols-3 gap-4">
            {/* <img
              src={IMAGES.culture}
              alt="Culture traditionnelle du Niger"
              className="rounded-xl shadow-lg w-full h-64 object-cover"
            /> */}
            {ii?.map((image, index) => (
              <div
                key={index}
                className="relative group overflow-hidden rounded-lg"
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-60 object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                  <div className="p-4 text-white">
                    <h5 className="font-medium">{image.title}</h5>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    cuisine: {
      title: "Cuisine & Épices",
      content: (
        <div className="space-y-4">
          <p>
            La cuisine nigérienne est riche en saveurs et en variété, reflétant
            la diversité culturelle du pays. Elle est basée sur des céréales
            comme le mil et le sorgho, complétées par des légumes, des épices et
            occasionnellement de la viande ou du poisson.
          </p>
          <h3
            className="text-xl font-semibold mt-6 mb-3"
            style={{ color: COLORS.green }}
          >
            Le Yaji - L'épice emblématique
          </h3>
          <p>
            Le Yaji, aussi connu sous le nom de "Suya spice", est un mélange
            d'épices caractéristique de la cuisine nigérienne. Composé
            principalement de piment, d'arachide moulue, de gingembre et
            d'épices locales, il est utilisé pour assaisonner les viandes
            grillées (suya) et de nombreux plats traditionnels.
          </p>
          <p>
            Ce mélange unique offre une explosion de saveurs à la fois
            piquantes, légèrement sucrées et aromatiques. Son goût distinctif
            est devenu un symbole de la cuisine d'Afrique de l'Ouest et est très
            apprécié pour sa capacité à transformer des plats ordinaires en
            expériences gustatives extraordinaires.
          </p>
          <div className="mt-6">
            <img
              src={IMAGES.spices}
              alt="Épices traditionnelles du Niger, dont le Yaji"
              className="rounded-xl shadow-lg w-full h-64 object-cover"
            />
          </div>
        </div>
      ),
    },
    crickets: {
      title: "Criquets  & Insectes comestibles",
      content: (
        <div className="space-y-4">
          <p>
            Les Criquets sont une spécialité culinaire et une source ancestrale
            de protéines au Niger. Consommés depuis des générations, ces
            insectes comestibles font partie de l'alimentation traditionnelle et
            sont très appréciés pour leur goût et leur valeur nutritionnelle.
          </p>
          <p>
            Riches en protéines (jusqu'à 65%), en vitamines, en minéraux et en
            acides gras essentiels, les Criquets constituent une alternative
            écologique et durable à la viande conventionnelle. Leur empreinte
            environnementale est considérablement réduite par rapport à
            l'élevage traditionnel.
          </p>
          <h3
            className="text-xl font-semibold mt-6 mb-3"
            style={{ color: COLORS.orange }}
          >
            Préparation traditionnelle
          </h3>
          <p>
            Au Niger, les Criquets sont généralement récoltées tôt le matin,
            puis nettoyées et préparées selon diverses recettes. Elles peuvent
            être grillées avec du sel et des épices, frites avec de l'ail et du
            piment, ou séchées pour être conservées plus longtemps. Les Criquets
            que nous proposons sont préparées selon ces méthodes
            traditionnelles, garantissant leur authenticité et leur qualité.
          </p>
          <div className="mt-6">
            <img
              src={IMAGES.crickets}
              alt="Criquets  comestibles du Niger"
              className="rounded-xl shadow-lg w-full h-64 object-cover"
            />
          </div>
        </div>
      ),
    },
    artisans: {
      title: "Artisanat & Savoir-faire",
      content: (
        <div className="space-y-4">
          <p>
            L'artisanat nigérien est l'expression d'un riche patrimoine culturel
            transmis de génération en génération. Chaque pièce raconte une
            histoire, porte des symboles et témoigne d'un savoir-faire
            ancestral.
          </p>
          <p>
            Les artisans touaregs sont célèbres pour leur travail du cuir et de
            l'argent, créant des bijoux, des sacs et des objets décoratifs d'une
            finesse remarquable. La vannerie nigérienne transforme les fibres
            naturelles en paniers, nattes et objets utilitaires aux motifs
            géométriques complexes.
          </p>
          <p>
            La poterie, principalement réalisée par les femmes, combine
            fonctionnalité et beauté avec des techniques transmises depuis des
            siècles. Le tissage produit des étoffes colorées comme le
            "tera-tera" aux motifs distinctifs qui servent à la confection de
            vêtements traditionnels.
          </p>
          <p>
            En achetant ces créations, vous soutenez directement ces artisans et
            contribuez à la préservation de techniques ancestrales menacées par
            la production industrielle moderne.
          </p>
          <div className="mt-6">
            <img
              src={IMAGES.artisans}
              alt="Artisans nigériens au travail"
              className="rounded-xl shadow-lg w-full h-64 object-cover"
            />
          </div>
        </div>
      ),
    },
  };

  return (
    <>
      <HomeHeader2 />

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section avec image panoramique du Niger */}
        <div className="relative h-96">
          <div className="absolute inset-0">
            <img
              src={IMAGES.hero}
              alt="Panorama du Niger"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-green-900/80 to-transparent"></div>
          </div>
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="max-w-2xl text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Découvrez le Niger
              </h1>
              <p className="text-xl opacity-90 mb-6">
                Terre de traditions, de saveurs et d'artisanat au cœur de
                l'Afrique de l'Ouest
              </p>
              <div className="flex space-x-3">
                <a
                  href="#about"
                  className="px-6 py-3 rounded-full font-medium bg-white text-green-800 hover:bg-green-50 transition shadow-md"
                >
                  En savoir plus
                </a>
                <a
                  href="/produits"
                  className="px-6 py-3 rounded-full font-medium text-white border-2 border-white hover:bg-white/10 transition"
                >
                  Nos produits
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Introduction et carte */}
        <section id="about" className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div
                  className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-6"
                  style={{
                    backgroundColor: COLORS.lightGreen,
                    color: COLORS.green,
                  }}
                >
                  Présentation
                </div>
                <h2 className="text-3xl font-bold mb-6">
                  Le Niger, un pays aux multiples facettes
                </h2>
                <p className="text-gray-600 mb-4">
                  Situé au cœur de l'Afrique de l'Ouest, le Niger est un pays
                  riche en traditions, en ressources naturelles et en diversité
                  culturelle. Avec une superficie de plus de 1,2 million de km²,
                  c'est le plus vaste pays d'Afrique de l'Ouest.
                </p>
                <p className="text-gray-600 mb-6">
                  Le fleuve Niger, qui lui donne son nom, traverse l'ouest du
                  pays sur environ 550 km et constitue une source vitale pour
                  l'agriculture et la pêche. Le paysage nigérien varie des
                  régions désertiques du Sahara au nord aux savanes du sud, en
                  passant par le massif de l'Aïr aux formations rocheuses
                  spectaculaires.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex items-start">
                    <div
                      className="flex-shrink-0 p-2 rounded-full"
                      style={{ backgroundColor: COLORS.lightGreen }}
                    >
                      <Heart
                        className="w-5 h-5"
                        style={{ color: COLORS.green }}
                      />
                    </div>
                    <div className="ml-3">
                      <h3 className="font-medium">Hospitalité</h3>
                      <p className="text-sm text-gray-500">
                        L'accueil chaleureux est au cœur de la culture
                        nigérienne
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div
                      className="flex-shrink-0 p-2 rounded-full"
                      style={{ backgroundColor: COLORS.lightOrange }}
                    >
                      <Coffee
                        className="w-5 h-5"
                        style={{ color: COLORS.orange }}
                      />
                    </div>
                    <div className="ml-3">
                      <h3 className="font-medium">Gastronomie</h3>
                      <p className="text-sm text-gray-500">
                        Des saveurs uniques entre tradition et innovation
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex items-start">
                    <div
                      className="flex-shrink-0 p-2 rounded-full"
                      style={{ backgroundColor: COLORS.lightGreen }}
                    >
                      <Award
                        className="w-5 h-5"
                        style={{ color: COLORS.green }}
                      />
                    </div>
                    <div className="ml-3">
                      <h3 className="font-medium">Artisanat</h3>
                      <p className="text-sm text-gray-500">
                        Un savoir-faire ancestral transmis de génération en
                        génération
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div
                      className="flex-shrink-0 p-2 rounded-full"
                      style={{ backgroundColor: COLORS.lightOrange }}
                    >
                      <Wind
                        className="w-5 h-5"
                        style={{ color: COLORS.orange }}
                      />
                    </div>
                    <div className="ml-3">
                      <h3 className="font-medium">Patrimoine</h3>
                      <p className="text-sm text-gray-500">
                        Une richesse culturelle façonnée par l'histoire
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="relative">
                  <img
                    src={IMAGES.map}
                    alt="Carte du Niger"
                    className="w-full rounded-xl shadow-lg"
                  />
                  <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-lg shadow-lg">
                    <div className="flex items-center space-x-2">
                      <Map
                        className="w-5 h-5"
                        style={{ color: COLORS.green }}
                      />
                      <span className="font-medium">Afrique de l'Ouest</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Statistiques animées */}
        <section
          className="py-16"
          style={{ backgroundColor: COLORS.paleGreen }}
        >
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <div
                  className="text-3xl font-bold mb-2"
                  style={{ color: COLORS.green }}
                >
                  {animatedCount.population} M
                </div>
                <p className="text-gray-600">Population</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <div
                  className="text-3xl font-bold mb-2"
                  style={{ color: COLORS.orange }}
                >
                  {animatedCount.area.toLocaleString()} km²
                </div>
                <p className="text-gray-600">Superficie</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <div
                  className="text-3xl font-bold mb-2"
                  style={{ color: COLORS.green }}
                >
                  {animatedCount.yearFounded}
                </div>
                <p className="text-gray-600">Indépendance</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <div
                  className="text-3xl font-bold mb-2"
                  style={{ color: COLORS.orange }}
                >
                  {animatedCount.ethnicGroups}+
                </div>
                <p className="text-gray-600">Groupes ethniques</p>
              </div>
            </div>
          </div>
        </section>

        {/* Onglets d'information */}
        {/* <RichessesNigerSection /> */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <div
                className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-4"
                style={{
                  backgroundColor: COLORS.lightOrange,
                  color: COLORS.orange,
                }}
              >
                Découvrir
              </div>
              <h2 className="text-3xl font-bold">Richesses du Niger</h2>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="flex flex-wrap justify-center mb-8 gap-2">
                {Object.keys(tabContent).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                      activeTab === tab
                        ? "text-white"
                        : "text-gray-700 bg-gray-100 hover:bg-gray-200"
                    }`}
                    style={{
                      backgroundColor:
                        activeTab === tab
                          ? tab === "culture" || tab === "artisans"
                            ? COLORS.green
                            : COLORS.orange
                          : "",
                    }}
                  >
                    {tabContent[tab].title}
                  </button>
                ))}
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
                <h3 className="text-2xl font-bold mb-6">
                  {tabContent[activeTab].title}
                </h3>
                {tabContent[activeTab].content}
              </div>
            </div>
          </div>
        </section>

        {/* Notre mission */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div
                  className="p-8 md:p-10"
                  style={{ backgroundColor: COLORS.green }}
                >
                  <h2 className="text-3xl font-bold text-white mb-6">
                    Notre mission
                  </h2>
                  <p className="text-white/90 mb-4">
                    Chez Kassarmou, notre mission est simple et profonde :
                    rapprocher les Nigériens de leur culture, où qu’ils soient
                    dans le monde. Nous croyons que les objets du quotidien — un
                    plat, une épice, un tissu, une œuvre artisanale — peuvent
                    porter en eux une mémoire, une histoire, un lien vivant avec
                    nos origines. Kassarmou facilite l’accès à une sélection
                    rigoureuse de produits nigériens authentiques, spécialement
                    pensée pour la diaspora. Nous permettons à chacun de
                    retrouver les goûts, les savoir-faire et les symboles qui
                    font l’âme du Niger, directement chez soi, avec simplicité
                    et confiance.
                  </p>
                  <p className="text-white/90 mb-6">
                    Au-delà du commerce, Kassarmou est un projet culturel et
                    identitaire. Nous nous engageons à promouvoir, valoriser et
                    transmettre la richesse de la culture nigérienne à travers
                    chaque produit. Chaque commande est un acte de mémoire, de
                    partage et de fierté pour la diaspora nigérienne.
                  </p>
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center mr-4">
                      <ShoppingBag className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium">
                        Commerce équitable
                      </h3>
                      <p className="text-white/70 text-sm">
                        Pour un développement durable
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-8 md:p-10">
                  <h3
                    className="text-xl font-bold mb-4"
                    style={{ color: COLORS.green }}
                  >
                    Nos valeurs
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <ChevronRight
                        className="w-5 h-5 mt-0.5 flex-shrink-0"
                        style={{ color: COLORS.orange }}
                      />
                      <div className="ml-2">
                        <h4 className="font-medium">Authenticité</h4>
                        <p className="text-gray-600 text-sm">
                          Nous proposons uniquement des produits traditionnels,
                          préparés selon des méthodes ancestrales.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <ChevronRight
                        className="w-5 h-5 mt-0.5 flex-shrink-0"
                        style={{ color: COLORS.orange }}
                      />
                      <div className="ml-2">
                        <h4 className="font-medium">Durabilité</h4>
                        <p className="text-gray-600 text-sm">
                          Nous privilégions des pratiques respectueuses de
                          l'environnement à chaque étape de notre chaîne
                          d'approvisionnement.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <ChevronRight
                        className="w-5 h-5 mt-0.5 flex-shrink-0"
                        style={{ color: COLORS.orange }}
                      />
                      <div className="ml-2">
                        <h4 className="font-medium">Transparence</h4>
                        <p className="text-gray-600 text-sm">
                          Nous partageons ouvertement l'origine de nos produits
                          et les conditions de leur production.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <ChevronRight
                        className="w-5 h-5 mt-0.5 flex-shrink-0"
                        style={{ color: COLORS.orange }}
                      />
                      <div className="ml-2">
                        <h4 className="font-medium">Impact social</h4>
                        <p className="text-gray-600 text-sm">
                          Nous investissons dans les communautés locales pour
                          favoriser leur développement économique.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold mb-6">
                Prêt à découvrir les saveurs du Niger ?
              </h2>
              <p className="text-gray-600 mb-8">
                Explorez notre gamme de produits authentiques et soutenez les
                communautés locales du Niger tout en vivant une expérience
                gustative unique.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <a
                  href="/produits"
                  className="px-8 py-3 rounded-full font-medium text-white shadow-md hover:shadow-lg transition-shadow"
                  style={{ backgroundColor: COLORS.orange }}
                >
                  Explorer nos produits
                </a>
                <a
                  href="/ContactPage"
                  className="px-8 py-3 rounded-full font-medium shadow-md hover:shadow-lg transition-shadow"
                  style={{
                    backgroundColor: COLORS.lightGreen,
                    color: COLORS.green,
                  }}
                >
                  Nous contacter
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
